import React, { useState, useEffect } from "react";
import Option from "./variants_form/Option";
import { generateVariants, filterEmptyValues, REWARD_TYPES, generateId } from "./variants_form/utils";
import VariantTable from "./variants_form/VariantTable";
import { updateRewardQuantityFromVariants } from "./variants_form/quantityUpdater";
import { 
  handleInitialAddOption, 
  handleAddOption, 
  handleOptionNameChange, 
  handleOptionValueChange, 
  handleDeleteValue, 
  handleDeleteOption,
  handleToggleValueDisabled,
  handleDone, 
  handleEdit, 
  handleImageUpload, 
  handleVariantImageUpload, 
  updateVariant 
} from "./variants_form/handlers";

const useInitialSetup = (preloadedOptions, preloadedVariants, opts) => {
  const [options, setOptions] = useState([]);
  const [variants, setVariants] = useState([]);
  const [showOptions, setShowOptions] = useState(false);
  const [editModes, setEditModes] = useState({});
  const [groupBy, setGroupBy] = useState('');
  const canAddVariants = gon.can_add_variants;

  useEffect(() => {
    setVariants(preloadedVariants);
    setOptions(preloadedOptions);
    
    const initialEditModes = {};
    preloadedOptions.forEach((option) => {
      initialEditModes[option.id] = false;
      if (opts?.reward_type === REWARD_TYPES.VARIABLE_TYPE) {
        initialEditModes[option.id] = true;
      }
    });
    setEditModes(initialEditModes);

    if (preloadedOptions?.length > 0) {
      setGroupBy(preloadedOptions[0].title);
      setShowOptions(true);
    }

    setOptions(prevOptions => {
      return prevOptions.map(option => {
        const hasEmptyValue = option.values.some(value => value.name === '');
        if (!hasEmptyValue && (opts?.reward_type === REWARD_TYPES.COMPANY_FULFILLED || opts?.reward_type === REWARD_TYPES.VARIABLE_TYPE)) {
          return {
            ...option,
            values: [...option.values, { id: generateId(), name: '', sku: '', image: null, disabled: false }]
          };
        }
        return option;
      });
    });
  }, []);

  return { options, setOptions, variants, setVariants, showOptions, setShowOptions, editModes, setEditModes, groupBy, setGroupBy, canAddVariants };
};

const useOptionsEffect = (options, variants, setVariants, canAddVariants, opts, groupBy, setGroupBy) => {
  useEffect(() => {
    const hiddenInput = document.getElementById('options_input');
    if (hiddenInput) {
      hiddenInput.value = JSON.stringify(filterEmptyValues(options));
    }

    if (options.length > 0) {
      generateVariants(options, variants, setVariants, canAddVariants, opts);
    }

    if (options.length > 1 && !groupBy) {
      setGroupBy(options[0].title);
    }
  }, [options]);
};

const useVariantsEffect = (variants) => {
  useEffect(() => {
    const hiddenInput = document.getElementById('variants_input');
    if (hiddenInput) {
      hiddenInput.value = JSON.stringify(variants);
    }
    
    // Update reward quantity based on variant quantities
    updateRewardQuantityFromVariants(variants);
  }, [variants]);
};

const VariantsForm = ({ preloadedOptions, preloadedVariants, opts }) => {
  const {
    options,
    setOptions,
    variants,
    setVariants,
    showOptions,
    setShowOptions,
    editModes,
    setEditModes,
    groupBy,
    setGroupBy,
    canAddVariants
  } = useInitialSetup(preloadedOptions, preloadedVariants, opts);

  useOptionsEffect(options, variants, setVariants, canAddVariants, opts, groupBy, setGroupBy);
  useVariantsEffect(variants);

  const renderInitialAddButton = () => (
    !canAddVariants && (
      <button 
        type="button"
        className="btn btn-link text-decoration-none text-primary p-0"
        onClick={(e) => handleInitialAddOption(e, setShowOptions, options, setOptions, setEditModes)}
      >
        + Add options like size or color
      </button>
    )
  );

  const renderOptionsList = () => (
    <div className="options-wrapper">
      <label className="form-label fw-bold">Options</label>
      {options.sort((a, b) => a.order - b.order).map((option, index) => (
        <Option
          key={option.id}
          option={option}
          index={index}
          options={options}
          editModes={editModes}
          setEditModes={setEditModes}
          handleOptionNameChange={handleOptionNameChange}
          handleOptionValueChange={handleOptionValueChange}
          handleDeleteValue={handleDeleteValue}
          handleDeleteOption={handleDeleteOption}
          handleToggleValueDisabled={handleToggleValueDisabled}
          handleDone={handleDone}
          handleEdit={handleEdit}
          canAddVariants={canAddVariants}
          setOptions={setOptions}
          setVariants={setVariants}
          opts={opts}
        />
      ))}

      {options.length < 3 && !canAddVariants && opts?.reward_type === REWARD_TYPES.COMPANY_FULFILLED && (
        <button 
          className="btn btn-link text-decoration-none text-secondary p-0 mt-3"
          onClick={(e) => handleAddOption(e, options, setOptions, setEditModes)}
        >
          <div className="d-flex align-items-center gap-2">
            <span className="text-primary">Add another option</span>
          </div>
        </button>
      )}
    </div>
  );

  const renderVariantTable = () => (
    variants.length > 0 && (
      <VariantTable
        options={options}
        setOptions={setOptions}
        variants={variants}
        setVariants={setVariants}
        groupBy={groupBy}
        setGroupBy={setGroupBy}
        handleImageUpload={handleImageUpload}
        handleVariantImageUpload={handleVariantImageUpload}
        handleOptionValueChange={handleOptionValueChange}
        updateVariant={updateVariant}
        opts={opts}
        canAddVariants={canAddVariants}
      />
    )
  );

  return (
    <div className="card">
      <div className="p-2 options-container">
        {!showOptions ? renderInitialAddButton() : renderOptionsList()}
      </div>
      {renderVariantTable()}
    </div>
  );
};

export default VariantsForm;